import { z } from 'zod';

/**
 * Phase 11 (MEDIA-01..03) — Zod schemas for the avatar/logo upload flow.
 *
 * Shared between the API (`createZodDto` via nestjs-zod) and the api-client
 * (`responseSchema` for parse). One source of truth across the boundary.
 */

/** Server-side allowlist of accepted upload MIME types (Pitfall #3). */
export const UploadMimeTypeSchema = z.enum([
  'image/jpeg',
  'image/png',
  'image/webp',
]);

/** 5 MB hard cap — also baked into the AWS Sig v4 signature at presign time. */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

export const UploadKindSchema = z.enum(['avatar', 'logo']);

/**
 * POST /media/presigned-url request body.
 *
 * `kind` discriminates avatar vs logo. The server further validates that
 * the caller's JWT role matches: PROFISSIONAL for avatar, EMPRESA for logo
 * (T-11-01 — role-bound presign issuance).
 */
export const PresignedUrlRequestSchema = z.object({
  kind: UploadKindSchema,
  mimeType: UploadMimeTypeSchema,
  sizeBytes: z.number().int().positive().max(MAX_UPLOAD_BYTES),
});

/**
 * POST /media/presigned-url response body.
 *
 * `publicUrl` is `null` at presign time — the canonical URL is determined
 * after Sharp post-processing in the finalize endpoint.
 */
export const PresignedUrlResponseSchema = z.object({
  uploadUrl: z.string().url(),
  key: z.string(),
  expiresIn: z.number().int(),
  publicUrl: z.string().url().nullable(),
});

/**
 * POST /profissionais/me/avatar request body.
 *
 * `rawKey` must start with `avatars/profissional/` — the server further
 * verifies the path prefix matches the JWT-extracted profissionalId
 * (T-11-02 — cross-tenant rawKey defense).
 */
export const SetAvatarRequestSchema = z.object({
  rawKey: z.string().startsWith('avatars/profissional/'),
});

/**
 * POST /empresas/:id/logo request body.
 *
 * `rawKey` must start with `logos/empresa/` — controller further verifies
 * the empresaId path param matches both the JWT claim AND the rawKey prefix.
 */
export const SetLogoRequestSchema = z.object({
  rawKey: z.string().startsWith('logos/empresa/'),
});

/**
 * Response shape for both finalize endpoints — the canonical public URL of
 * the processed image. Clients store this and use it as the `<img src>`.
 */
export const MediaUploadResponseSchema = z.object({
  publicUrl: z.string().url(),
});

export type UploadMimeType = z.infer<typeof UploadMimeTypeSchema>;
export type UploadKind = z.infer<typeof UploadKindSchema>;
export type PresignedUrlRequest = z.infer<typeof PresignedUrlRequestSchema>;
export type PresignedUrlResponse = z.infer<typeof PresignedUrlResponseSchema>;
export type SetAvatarRequest = z.infer<typeof SetAvatarRequestSchema>;
export type SetLogoRequest = z.infer<typeof SetLogoRequestSchema>;
export type MediaUploadResponse = z.infer<typeof MediaUploadResponseSchema>;
